import { NOTIFICATION_TYPES, parseMoneyValue } from "../utils";

export class AccountForecastService {
  constructor(apiClient, showToast) {
    this.apiClient = apiClient;
    this.showToast = showToast;
  }

  /**
   * Fetches and combines account forecast data from multiple sources
   * @param {number} accountId - The account ID
   * @returns {Object} Combined forecast data
   */
  async getAccountForecastData(accountId) {
    if (!accountId) {
      throw new Error("Account ID is required");
    }

    try {
      // Fetch data from multiple sources in parallel
      const [
        accountResponse,
        forecastResponse,
        expansionOpportunitiesResponse,
      ] = await Promise.allSettled([
        this.apiClient.get(`accounts/${accountId}/`),
        this.apiClient.get(`metrics/account-forecast/${accountId}/`),
        this.apiClient.get(`accounts/${accountId}/expansion-opportunities/`),
      ]);

      // Handle account data
      const accountData =
        accountResponse.status === "fulfilled"
          ? accountResponse.value.data
          : null;

      // Handle forecast metrics data
      const forecastData =
        forecastResponse.status === "fulfilled"
          ? forecastResponse.value.data
          : null;

      // Handle expansion opportunities data
      const expansionData =
        expansionOpportunitiesResponse.status === "fulfilled"
          ? expansionOpportunitiesResponse.value.data.results ||
            expansionOpportunitiesResponse.value.data
          : [];

      // Log any failed requests
      if (accountResponse.status === "rejected") {
        console.error("Failed to fetch account data:", accountResponse.reason);
      }
      if (forecastResponse.status === "rejected") {
        console.error(
          "Failed to fetch forecast data:",
          forecastResponse.reason,
        );
      }
      if (expansionOpportunitiesResponse.status === "rejected") {
        console.error(
          "Failed to fetch expansion opportunities:",
          expansionOpportunitiesResponse.reason,
        );
      }

      // Combine and organize the data
      return this._combineAccountForecastData(
        accountData,
        forecastData,
        expansionData,
      );
    } catch (error) {
      console.error("Error fetching account forecast data:", error);
      this.showToast({
        content: "Failed to fetch account forecast data",
        type: NOTIFICATION_TYPES.error,
      });
      throw error;
    }
  }

  /**
   * Get account forecast metrics including renewal forecast, expansion potential, and CSM notes
   * @param {number} accountId - The account ID to get forecast metrics for
   * @returns {Promise} - Promise that resolves to the account forecast data
   */
  async getAccountForecastMetrics(accountId) {
    try {
      const response = await this.apiClient.get(
        `/metrics/account-forecast/${accountId}/`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching account forecast metrics:", error);
      throw error;
    }
  }

  async updateAccountForecastNote(accountId, forecastNote) {
    try {
      const response = await this.apiClient.patch(`/accounts/${accountId}/`, {
        forecast_note: forecastNote,
      });

      return response.data;
    } catch (error) {
      console.error("Error updating account forecast note:", error);
      throw error;
    }
  }
  /**
   * Updates the forecast note for an account
   * @param {number} accountId - The account ID
   * @param {string} note - The forecast note to save
   * @returns {Object} Updated account data
   */
  async updateForecastNote(accountId, note) {
    if (!accountId) {
      throw new Error("Account ID is required");
    }

    try {
      const response = await this.apiClient.patch(`accounts/${accountId}/`, {
        forecast_note: note,
      });

      this.showToast({
        content: "Renewal forecast note updated successfully",
        type: NOTIFICATION_TYPES.success,
      });

      return response.data;
    } catch (error) {
      console.error("Error updating forecast note:", error);
      this.showToast({
        content: "Failed to update forecast note",
        type: NOTIFICATION_TYPES.error,
      });
      throw error;
    }
  }

  /**
   * Private method to combine data from different sources
   * @param {Object} accountData - Account data
   * @param {Object} forecastData - Forecast metrics data
   * @param {Array} expansionData - Expansion opportunities data
   * @returns {Object} Combined and organized data
   */
  _combineAccountForecastData(accountData, forecastData, expansionData) {
    // Calculate expansion potential MRR
    const expansionPotentialMRR = Array.isArray(expansionData)
      ? expansionData.reduce(
          (acc, opp) => acc + parseMoneyValue(opp.additional_mrr).value,
          0,
        )
      : 0;

    // Determine if there's expansion potential
    const hasExpansionPotential =
      Array.isArray(expansionData) && expansionData.length > 0;

    // Get forecast note from account data (priority) or forecast data
    const forecastNote =
      accountData?.forecast_note || forecastData?.forecast_note || null;

    // Get renewal forecast amount - backend returns renewal_forecast_amount
    const renewalForecastAmount = forecastData?.renewal_forecast_amount || 0;

    return {
      // Account information
      account: {
        id: accountData?.id,
        name: accountData?.name,
        forecast_note: forecastNote,
      },

      // Forecast metrics
      forecast: {
        status: forecastData?.renewal_forecast_status || "unknown",
        mrr: renewalForecastAmount,
        confidence: forecastData?.confidence || null,
        last_updated: forecastData?.last_updated || null,
      },

      // Expansion data
      expansion: {
        has_potential: hasExpansionPotential,
        potential_mrr: expansionPotentialMRR,
        opportunities_count: Array.isArray(expansionData)
          ? expansionData.length
          : 0,
        opportunities: Array.isArray(expansionData) ? expansionData : [],
      },

      // Combined insights
      insights: {
        forecast_note: forecastNote,
        renewal_forecast_trend: forecastData?.renewal_forecast_trend || null,
        renewal_forecast_amount: renewalForecastAmount,
        expansion_potential:
          forecastData?.expansion_potential ||
          (hasExpansionPotential ? "yes" : "no"),
        expansion_potential_mrr:
          forecastData?.expansion_potential_mrr || expansionPotentialMRR,
        total_potential_value: renewalForecastAmount + expansionPotentialMRR,
        risk_level: this._calculateRiskLevel(
          forecastData?.renewal_forecast_status,
          hasExpansionPotential,
        ),
      },

      // Raw data for advanced usage
      raw: {
        account: accountData,
        forecast: forecastData,
        expansion: expansionData,
      },
    };
  }

  /**
   * Helper method to calculate risk level based on forecast status and expansion potential
   * @param {string} forecastStatus - The forecast status
   * @param {boolean} hasExpansionPotential - Whether there's expansion potential
   * @returns {string} Risk level
   */
  _calculateRiskLevel(forecastStatus, hasExpansionPotential) {
    if (forecastStatus === "churn_risk") return "high";
    if (forecastStatus === "down") return "medium";
    if (forecastStatus === "flat" && !hasExpansionPotential) return "medium";
    if (forecastStatus === "growth" || hasExpansionPotential) return "low";
    return "unknown";
  }

  /**
   * Helper method to get display information for forecast status
   * @param {string} status - The forecast status (supports both snake_case and human-readable formats)
   * @returns {Object} Display information
   */
  static getForecastStatusDisplay(status) {
    // Handle null/undefined as "Not Assessed"
    if (status == null) {
      return {
        text: "Not Assessed",
        bgColor: "bg-gray-100",
        textColor: "text-gray-600",
      };
    }

    const normalizedStatus = status.toLowerCase().trim();

    // Status map supporting both snake_case keys and human-readable strings
    const statusMap = {
      // Growth statuses
      growth: {
        text: "Growth",
        bgColor: "bg-green-100",
        textColor: "text-emerald-900",
      },
      // Flat statuses
      flat: {
        text: "Flat",
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-900",
      },
      // Downscale statuses
      down: {
        text: "Downscale",
        bgColor: "bg-red-100",
        textColor: "text-red-900",
      },
      downscale: {
        text: "Downscale",
        bgColor: "bg-red-100",
        textColor: "text-red-900",
      },
      // Churned statuses
      churned: {
        text: "Churned",
        bgColor: "bg-red-100",
        textColor: "text-red-900",
      },
      // Churn Expected statuses (both snake_case and human-readable)
      communicated_churn: {
        text: "Churn Expected",
        bgColor: "bg-red-100",
        textColor: "text-red-900",
      },
      "churn expected": {
        text: "Churn Expected",
        bgColor: "bg-red-100",
        textColor: "text-red-900",
      },
      // Not Assessed statuses
      not_assessed: {
        text: "Not Assessed",
        bgColor: "bg-gray-100",
        textColor: "text-gray-600",
      },
      "not assessed": {
        text: "Not Assessed",
        bgColor: "bg-gray-100",
        textColor: "text-gray-600",
      },
    };

    return (
      statusMap[normalizedStatus] || {
        text: "Not Assessed",
        bgColor: "bg-gray-100",
        textColor: "text-gray-600",
      }
    );
  }

  async getAccount(accountId) {
    try {
      const response = await this.apiClient.get(`/accounts/${accountId}/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching account data:", error);
      throw error;
    }
  }
}
